import { useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
// Api
import { IS_USER_FOLLOWING_STORE } from 'api/follow/queries';
import { FOLLOW_STORE, UNFOLLOW_STORE } from 'api/follow/mutations';
// Hooks
import { useAppContext, useGetCurrUser, useRefetchOnUserLogIn } from 'hooks';
// Types
import {
  IsUserFollowingStore,
  IsUserFollowingStoreVariables,
} from 'api/follow/types/IsUserFollowingStore';
import {
  FollowStores,
  FollowStoresVariables,
} from 'api/follow/types/FollowStores';
import {
  UnfollowStores,
  UnfollowStoresVariables,
} from 'api/follow/types/UnfollowStores';
import { GetStream_stream_store } from 'api/streams/types/GetStream';
import {
  GetMediaPost_getMediaPost_VideoPost_store,
  GetMediaPost_getMediaPost_ImagePost_store,
} from 'api/mediaPost/types/GetMediaPost';
import { GetStoreBySlug_store } from 'api/store/types/GetStoreBySlug';
import { GetStores_stores_entities } from 'api/store/types/GetStores';
import { GetStoreMerchProduct_getStoreMerchProduct_store } from 'api/merch/types/GetStoreMerchProduct';
import { GetMemorabiliaBySlug_getMemorabiliaBySlug_store } from 'api/memorabilia/types/GetMemorabiliaBySlug';
import { GetExperienceBySlug_getExperienceBySlug_store } from 'api/experiences/types/GetExperienceBySlug';
import { PublicTicketEventFields_host } from 'api/ticketEvents/types/PublicTicketEventFields';
// UI
import Button, { ButtonSize } from 'ui3/Button/Button';
import { IconNames } from 'ui3/MillionsIcon/MillionsIcon';
// Components
import { showToast } from 'components/common/Toast/Toast';

type FollowButtonProps = {
  store:
    | GetStoreBySlug_store
    | GetStream_stream_store
    | GetMediaPost_getMediaPost_VideoPost_store
    | GetMediaPost_getMediaPost_ImagePost_store
    | GetStores_stores_entities
    | GetStoreMerchProduct_getStoreMerchProduct_store
    | GetMemorabiliaBySlug_getMemorabiliaBySlug_store
    | GetExperienceBySlug_getExperienceBySlug_store
    | PublicTicketEventFields_host;
  startIcon?: IconNames;
  unfollowStartIcon?: IconNames;
  unfollowText: string;
  className: string;
  xs?: ButtonSize;
  onChange?: (isFollowing: boolean) => void;
  iconOnly?: boolean;
  followVariant?: 'primary' | 'secondary';
  unfollowVariant?: 'primary' | 'secondary';
  unfollowSubVariant?: 'outlined' | 'text';
};

const FollowButton = ({
  store,
  startIcon,
  unfollowStartIcon,
  unfollowText,
  className,
  xs = 'medium',
  onChange,
  iconOnly = false,
  followVariant = 'primary',
  unfollowVariant = 'secondary',
  unfollowSubVariant = 'outlined',
}: FollowButtonProps) => {
  const { data } = useGetCurrUser();
  const isLoggedIn = Boolean(data?.me?.id);
  const { setLoginModalVisibility } = useAppContext();

  const { data: followStatus, refetch } = useQuery<
    IsUserFollowingStore,
    IsUserFollowingStoreVariables
  >(IS_USER_FOLLOWING_STORE, {
    variables: {
      input: {
        storeId: store.id,
      },
    },
    skip: !isLoggedIn,
    fetchPolicy: 'network-only',
  });

  useRefetchOnUserLogIn({ refetch });

  const isFollowing = Boolean(followStatus?.isUserFollowingStore.isFollowing);

  const [followStore, { loading: followLoading }] = useMutation<
    FollowStores,
    FollowStoresVariables
  >(FOLLOW_STORE);

  const [unfollowStore, { loading: unfollowLoading }] = useMutation<
    UnfollowStores,
    UnfollowStoresVariables
  >(UNFOLLOW_STORE);

  const handleFollowStore = async () => {
    if (isLoggedIn) {
      try {
        await followStore({
          variables: {
            input: {
              storeIds: [store.id],
            },
          },
          refetchQueries: [
            {
              query: IS_USER_FOLLOWING_STORE,
              variables: {
                input: {
                  storeId: store.id,
                },
              },
            },
          ],
        });
      } catch (error) {
        showToast({
          message: (error as Error).message || 'Error following the store',
          type: 'error',
        });
      }
    } else {
      setLoginModalVisibility(true);
    }
  };

  useEffect(() => {
    onChange && onChange(isFollowing);
  }, [isFollowing, onChange]);

  const handleUnFollowStore = async () => {
    try {
      await unfollowStore({
        variables: {
          input: {
            storeIds: [store.id],
          },
        },
        refetchQueries: [
          {
            query: IS_USER_FOLLOWING_STORE,
            variables: {
              input: {
                storeId: store.id,
              },
            },
          },
        ],
      });
    } catch (error) {
      showToast({
        message: (error as Error).message || 'Error unfollowing the store',
        type: 'error',
      });
    }
  };

  return (
    <>
      {!isFollowing && (
        <Button
          loading={followLoading}
          variant={followVariant}
          className={className}
          onClick={handleFollowStore}
          size={xs}
          {...(startIcon && { icon: startIcon })}
        >
          {iconOnly ? '' : 'Follow'}
        </Button>
      )}
      {isFollowing && (
        <Button
          loading={unfollowLoading}
          variant={unfollowVariant}
          subVariant={unfollowSubVariant}
          className={className}
          onClick={handleUnFollowStore}
          size={xs}
          {...(unfollowStartIcon && { icon: unfollowStartIcon })}
        >
          {iconOnly ? '' : unfollowText}
        </Button>
      )}
    </>
  );
};
export default FollowButton;
